import React, { useCallback, useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { RefreshCw, Database, History, GitCompareArrows, CircleDot, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Disclaimer } from "@/components/common";

const STATUS_STYLE = {
    SYNCED: "bg-green-100 text-green-700 border-green-200",
    CHANGED: "bg-mineral/15 text-mineralHover border-mineral/30",
    PENDING: "bg-slate-100 text-slate-600 border-slate-200",
    UPDATING: "bg-blue-50 text-blue-700 border-blue-200",
    PROCESS_NOT_FOUND: "bg-amber-50 text-amber-700 border-amber-200",
    ANM_UNAVAILABLE: "bg-amber-50 text-amber-700 border-amber-200",
    ERROR: "bg-red-50 text-red-700 border-red-200",
};

const ANM_SOURCE_NOTE = "Fonte: SIGMINE — Dados Abertos da Agência Nacional de Mineração (ANM), arquivo BRASIL.zip. As informações possuem caráter informativo e devem ser confirmadas nos instrumentos oficiais da ANM quando necessário para produção de efeitos legais.";

const fmt = (v) => (v !== null && v !== undefined && String(v).trim() ? String(v) : "—");
const fmtDate = (v) => (v ? new Date(v).toLocaleString("pt-BR") : "—");

const Row = ({ label, value, wide }) => (
    <div className={`flex flex-col ${wide ? "sm:col-span-2 lg:col-span-3" : ""}`}>
        <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
        <span className="text-sm text-slate-800 font-medium mt-0.5 break-words">{fmt(value)}</span>
    </div>
);

export default function AnmPanel({ processId, processNumber, onSynced }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [hist, setHist] = useState(null);
    const [histLocked, setHistLocked] = useState(false);

    useEffect(() => {
        api.get(`/intelligence/history/${encodeURIComponent(processNumber || processId)}`)
            .then((r) => setHist(r.data))
            .catch((e) => { if (e.response?.status === 403) setHistLocked(true); });
    }, [processId, processNumber]);

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/processes/${processId}/anm`).then((r) => setData(r.data)).finally(() => setLoading(false));
    }, [processId]);
    useEffect(() => { load(); }, [load]);

    const sync = async () => {
        setSyncing(true);
        toast.info("Consultando Cadastro Mineiro da ANM...");
        try {
            const r = await api.post(`/processes/${processId}/anm/sync`);
            const baseDate = r.data?.base?.last_success ? new Date(r.data.base.last_success).toLocaleString("pt-BR") : "—";
            if (r.data?.status === "PROCESS_NOT_FOUND") {
                toast.warning(`Processo não localizado na base atual do SIGMINE/ANM. Base atualizada em: ${baseDate}.`);
            } else {
                const extra = [
                    r.data?.new_events ? `${r.data.new_events} nova(s) movimentação(ões)` : "",
                    r.data?.changes?.length ? `${r.data.changes.length} alteração(ões)` : "",
                ].filter(Boolean).join(" · ");
                toast.success(`Dados do Cadastro Mineiro atualizados com sucesso. Fonte: SIGMINE / ANM. Base atualizada em: ${baseDate}.${extra ? " " + extra + "." : ""}`);
            }
            load();
            onSynced && onSynced();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Não foi possível consultar a base do SIGMINE.");
        } finally { setSyncing(false); }
    };

    if (loading && !data) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-mineral mx-auto" /></div>;

    const c = data?.cadastro || {};
    const statusCls = STATUS_STYLE[data?.status] || STATUS_STYLE.PENDING;
    const baseDate = data?.base?.last_success ? new Date(data.base.last_success).toLocaleString("pt-BR") : null;
    const notFound = data?.status === "PROCESS_NOT_FOUND";
    const hasData = Object.keys(c).length > 0 && (c.PROCESSO || c.FASE || c.NOME);

    return (
        <div className="space-y-4" data-testid="anm-panel">
            <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Database className="w-5 h-5 text-petroleum" />
                        <div>
                            <div className="font-heading font-bold text-slate-800">Cadastro Mineiro (SIGMINE / ANM)</div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border ${statusCls}`} data-testid="anm-status">
                                    <CircleDot className="w-3 h-3" /> {data?.status_label}
                                </span>
                                <span className="text-xs text-slate-500">Última sincronização ANM: <b>{fmtDate(data?.last_sync)}</b></span>
                            </div>
                            {baseDate && <div className="text-[11px] text-slate-400 mt-0.5">Base SIGMINE atualizada em: <b>{baseDate}</b> · {fmt(data?.base?.record_count)} processos</div>}
                        </div>
                    </div>
                    <button onClick={sync} disabled={syncing} data-testid="anm-update-button" className="px-4 py-2 rounded-sm text-sm font-semibold bg-petroleum text-white hover:bg-petroleumHover transition-colors flex items-center gap-1 disabled:opacity-60">
                        {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Atualizar dados ANM
                    </button>
                </div>
            </div>

            <Tabs defaultValue="dados">
                <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
                    <TabsTrigger value="dados" data-testid="anm-tab-dados"><Database className="w-4 h-4 mr-1" /> Cadastro Mineiro</TabsTrigger>
                    <TabsTrigger value="eventos" data-testid="anm-tab-eventos"><History className="w-4 h-4 mr-1" /> Eventos ANM{data?.events?.length ? <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-mineral/15 text-mineralHover">{data.events.length}</span> : null}</TabsTrigger>
                    <TabsTrigger value="alteracoes" data-testid="anm-tab-alteracoes"><GitCompareArrows className="w-4 h-4 mr-1" /> Histórico de Alterações</TabsTrigger>
                    <TabsTrigger value="historico-gm2" data-testid="anm-tab-historico-gm2"><History className="w-4 h-4 mr-1" /> Histórico ANM (GM²)</TabsTrigger>
                </TabsList>

                <TabsContent value="dados" className="mt-4">
                    {notFound ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-md p-6 text-sm text-amber-800 flex items-start gap-3" data-testid="anm-not-found">
                            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                            <div>
                                <p className="font-semibold">Processo não localizado na base atual do SIGMINE/ANM.</p>
                                <p className="mt-1">Última atualização da base: <b>{baseDate || "—"}</b>. Verifique o número do processo ou aguarde a próxima atualização diária.</p>
                            </div>
                        </div>
                    ) : hasData ? (
                        <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="anm-cadastro">
                            <Row label="Número do processo" value={c.PROCESSO || c.DSProcesso || processNumber} />
                            <Row label="Ano" value={c.ANO} />
                            <Row label="Área (ha)" value={c.AREA_HA} />
                            <Row label="Fase" value={c.FASE} />
                            <Row label="UF" value={c.UF} />
                            <Row label="Tipo de uso" value={c.USO} />
                            <Row label="Titular / Nome" value={c.NOME} wide />
                            <Row label="Substâncias" value={c.SUBS} wide />
                            <div className="sm:col-span-2 lg:col-span-3 flex flex-col">
                                <span className="text-xs uppercase tracking-wide text-slate-400">Último evento</span>
                                <span className="text-sm text-slate-800 font-medium mt-0.5 break-words">{fmt(c.ULT_EVENTO)}</span>
                                <span className="text-[11px] text-slate-400 mt-1">As movimentações anteriores não são apagadas — todo histórico fica registrado na aba <b>Eventos ANM</b>{data?.events?.length ? ` (${data.events.length})` : ""}.</span>
                            </div>
                            <Row label="ID (ANM)" value={c.ID} />
                            <Row label="FID" value={c.FID} />
                        </div>
                    ) : (
                        <div className="text-sm text-slate-400 py-8 text-center border border-dashed border-slate-200 rounded-md">Nenhum dado carregado ainda. Clique em "Atualizar dados ANM".</div>
                    )}
                </TabsContent>

                <TabsContent value="eventos" className="mt-4">
                    {data?.events?.length ? (
                        <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm" data-testid="anm-events-timeline">
                            <ol className="relative border-l-2 border-slate-200 ml-2">
                                {data.events.map((e, i) => (
                                    <li key={e.id || i} className="ml-5 pb-5 last:pb-0">
                                        <span className="absolute -left-[9px] w-4 h-4 rounded-full bg-mineral border-2 border-white" />
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-bold text-slate-800 font-mono">{fmt(e.date)}</span>
                                            {e.is_new && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-mineral/15 text-mineralHover border border-mineral/30">Nova movimentação</span>}
                                        </div>
                                        <div className="text-sm text-slate-600 mt-0.5">{e.code ? <span className="font-mono text-slate-400 mr-1">[{e.code}]</span> : null}{fmt(e.description)}</div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ) : <div className="text-sm text-slate-400 py-8 text-center border border-dashed border-slate-200 rounded-md">Nenhum evento registrado ainda. Cada mudança do "último evento" na base do SIGMINE vira uma movimentação aqui.</div>}
                </TabsContent>

                <TabsContent value="alteracoes" className="mt-4">
                    {data?.changes?.length ? (
                        <div className="space-y-2" data-testid="anm-changes-list">
                            {data.changes.map((ch, i) => (
                                <div key={i} className="bg-white border border-slate-200 rounded-md p-3 text-sm">
                                    <div className="font-semibold text-slate-800">Alteração detectada — {ch.field}</div>
                                    <div className="text-slate-600 mt-1">Anterior: <span className="line-through text-slate-400">{fmt(ch.before)}</span> → Novo: <span className="text-mineralHover font-semibold">{fmt(ch.after)}</span></div>
                                    <div className="text-[11px] text-slate-400 mt-0.5">{fmtDate(ch.at)}</div>
                                </div>
                            ))}
                        </div>
                    ) : <div className="text-sm text-slate-400 py-8 text-center border border-dashed border-slate-200 rounded-md">Nenhuma alteração detectada até o momento.</div>}
                </TabsContent>

                <TabsContent value="historico-gm2" className="mt-4">
                    {histLocked ? (
                        <div className="bg-white border border-slate-200 rounded-md p-6 text-center" data-testid="anm-hist-locked">
                            <p className="font-semibold text-slate-800">Linha do tempo monitorada pelo GM²</p>
                            <p className="text-sm text-slate-500 mt-1">O histórico de alterações detectadas pelo GM² (mudança de fase, área, substância, disponibilidade) faz parte do <b>GM² Inteligência Mineral</b>.</p>
                            <a href="/inteligencia" className="inline-block mt-3 bg-mineral hover:bg-mineralHover text-white rounded-sm px-5 py-2 font-semibold transition-colors">Conhecer Inteligência Mineral</a>
                        </div>
                    ) : (hist?.items?.length ? (
                        <div className="bg-white border border-slate-200 rounded-md p-5 shadow-sm" data-testid="anm-hist-timeline">
                            {hist.monitorado_desde && <p className="text-[11px] text-slate-400 mb-3">Histórico monitorado pelo GM² desde {new Date(hist.monitorado_desde).toLocaleDateString("pt-BR")}.</p>}
                            <ol className="relative border-l-2 border-slate-200 ml-2">
                                {[...hist.items].reverse().map((e, i) => (
                                    <li key={i} className="ml-5 pb-5 last:pb-0">
                                        <span className="absolute -left-[9px] w-4 h-4 rounded-full bg-mineral border-2 border-white" />
                                        <div className="text-sm font-bold text-slate-800 font-mono">{e.data_deteccao ? new Date(e.data_deteccao).toLocaleDateString("pt-BR") : "—"}</div>
                                        <div className="text-sm text-slate-600 mt-0.5">{e.tipo_alteracao} {e.campo_alterado ? `— ${e.campo_alterado}` : ""}: <span className="text-slate-400">{String(e.valor_anterior || "—").slice(0, 40)}</span> → <span className="font-semibold text-mineralHover">{String(e.valor_atual || "—").slice(0, 40)}</span></div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ) : <div className="text-sm text-slate-400 py-8 text-center border border-dashed border-slate-200 rounded-md">Nenhuma alteração detectada pelo GM² ainda para este processo. As detecções aparecem conforme a base diária do SIGMINE muda.</div>)}
                </TabsContent>
            </Tabs>

            <Disclaimer>{ANM_SOURCE_NOTE}</Disclaimer>
        </div>
    );
}
